import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Pencil, Trash2 } from 'lucide-react';
import { cn, formatCurrency, formatDateTime, formatVolume } from '../../lib/utils';
import { libelle, referenceLisible } from '../../lib/libelles';
import { usePagination } from '../../hooks/usePagination';
import { BarrePagination } from '../ui/pagination-bar';

const StockMovementsTable = ({
  movements,
  isLoading,
  searchQuery,
  onSearchChange,
  // Un type de mouvement, quand l'écran en désigne un: « Filtrer les pertes »
  // annonçait un filtre et vidait la barre de recherche à la place. La
  // recherche textuelle ne pouvait pas le faire — elle lit une référence, une
  // note et un libellé, pas une nature de mouvement.
  typeFilter = null,
  onClearTypeFilter,
  onEdit,
  onDelete,
  variant = 'transport',
  // La recherche est posée par l'écran, sur la ligne des onglets.
  rechercheExterne = false,
}) => {
  const isControlFuel = variant === 'controlFuel';
  const filtered = movements.filter((m) => {
    if (typeFilter && m.movement_type !== typeFilter) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.reference_number?.toLowerCase().includes(q) ||
      m.notes?.toLowerCase().includes(q) ||
      movementTypeLabel(m.movement_type, isControlFuel).toLowerCase().includes(q)
    );
  });
  const pagination = usePagination(filtered, {
    reinitialiser: `${searchQuery || ''}|${typeFilter || ''}`,
    cle: 'mouvements',
  });

  const getMovementTypeBadge = (type) => {
    const colors = {
      IMPORT: 'bg-success/15 text-success',
      SALE: 'bg-info/15 text-info',
      INTERNAL_TRANSFER: 'bg-process/15 text-process',
      INTERNAL_CONSUMPTION: 'bg-flame/15 text-flame-ink',
      LOSS: 'bg-destructive/15 text-destructive',
      ADJUSTMENT: 'bg-flame/15 text-flame-ink',
    };
    return cn('status-badge', colors[type] || 'bg-muted text-muted-foreground');
  };

  return (
    <div className="space-y-3">
      <div className={cn('flex flex-wrap items-center gap-2', !rechercheExterne || typeFilter ? 'mb-3' : '')}>
        {rechercheExterne ? null : (
        <Input
          className="min-w-[12rem] flex-1 lg:max-w-[26rem] lg:flex-none lg:w-[24rem]"
          placeholder={isControlFuel ? 'Rechercher sortie, cuve, engin, note...' : 'Rechercher par référence...'}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
        />
        )}
        {/* Un filtre posé depuis un autre endroit de l'écran doit se voir et
            se retirer là où il agit, sinon la liste paraît incomplète sans
            qu'on sache pourquoi. */}
        {typeFilter ? (
          <button
            type="button"
            onClick={onClearTypeFilter}
            className="rounded-full border border-border/70 px-3 py-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {movementTypeLabel(typeFilter, isControlFuel)} ×
          </button>
        ) : null}
      </div>
      {isLoading ? (
        <p className="text-muted-foreground">Chargement...</p>
      ) : (
        <>
        <div className="hidden xl:block [&_td]:px-2 [&_th]:px-2">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Mouvement</TableHead>
              <TableHead className="text-right">Quantité</TableHead>
              <TableHead className="text-right">Coût / L</TableHead>
              <TableHead className="text-right">Valeur</TableHead>
              <TableHead>De → vers</TableHead>
              <TableHead>Référence</TableHead>
              <TableHead>Saisi par</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pagination.lignes.map((movement) => (
              <TableRow key={movement.id}>
                <TableCell className="whitespace-nowrap">{formatDateTime(movement.movement_date)}</TableCell>
                <TableCell>
                  <Badge className={getMovementTypeBadge(movement.movement_type)}>
                    {movementTypeLabel(movement.movement_type, isControlFuel)}
                  </Badge>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatVolume(movement.quantity_liters)}</TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">
                  {movement.unit_cost ? `${Number(movement.unit_cost).toFixed(2).replace('.', ',')} FCFA` : '—'}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right tabular-nums">
                  {/* Une valeur est un montant, pas un volume: `formatVolume`
                      suffixait « L » sur des francs. 800 L à 725 F donnaient
                      « 580 000 L », un chiffre juste avec la mauvaise unité —
                      c'est-à-dire un chiffre faux. */}
                  {movement.total_value
                    ? formatCurrency(movement.total_value)
                    : '—'}
                </TableCell>
                <TableCell className="whitespace-nowrap">
                  {locationLabel(movement.from_location, isControlFuel, 'from', movement)}
                  {' → '}
                  {locationLabel(movement.to_location, isControlFuel, 'to', movement)}
                </TableCell>
                <TableCell>{referenceLisible(movement.reference_number, '—')}</TableCell>
                <TableCell>
                  <Badge
                    className={cn(
                      'text-xs',
                      movement.source && movement.source !== 'MANUAL'
                        ? 'bg-info/10 text-info'
                        : 'bg-muted/50 text-muted-foreground'
                    )}
                  >
                    {libelle('stockSource', movement.source || 'MANUAL', 'Saisie à la main')}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Modifier le mouvement"
                      title="Modifier"
                      onClick={() => onEdit(movement)}
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label="Supprimer le mouvement"
                      title="Supprimer"
                      onClick={() => onDelete(movement)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
        <ul className="space-y-2 xl:hidden" data-testid="mouvements-cartes">
          {pagination.lignes.map((movement) => (
            <li key={movement.id} className="rounded-xl border border-border/70 bg-background p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Badge className={getMovementTypeBadge(movement.movement_type)}>
                  {movementTypeLabel(movement.movement_type, isControlFuel)}
                </Badge>
                <span className="text-xs text-muted-foreground">{formatDateTime(movement.movement_date)}</span>
              </div>
              <p className="mt-2 text-base font-bold tabular-nums text-foreground">{formatVolume(movement.quantity_liters)}</p>
              <p className="text-xs tabular-nums text-muted-foreground">
                {movement.unit_cost ? `${Number(movement.unit_cost).toFixed(2).replace('.', ',')} FCFA / L` : '—'}
                {movement.total_value ? ` · ${formatCurrency(movement.total_value)}` : ''}
              </p>
              <p className="mt-1 text-sm text-foreground">
                {locationLabel(movement.from_location, isControlFuel, 'from', movement)}
                {' → '}
                {locationLabel(movement.to_location, isControlFuel, 'to', movement)}
              </p>
              <p className="text-xs text-muted-foreground">
                Référence : {referenceLisible(movement.reference_number, '—')}
                {' · '}
                {libelle('stockSource', movement.source || 'MANUAL', 'Saisie à la main')}
              </p>
              <div className="mt-2 flex justify-end gap-2">
                <Button variant="outline" size="icon" className="h-10 w-10" aria-label="Modifier le mouvement" title="Modifier" onClick={() => onEdit(movement)}>
                  <Pencil className="size-4" />
                </Button>
                <Button variant="outline" size="icon" className="h-10 w-10" aria-label="Supprimer le mouvement" title="Supprimer" onClick={() => onDelete(movement)}>
                  <Trash2 className="size-4 text-destructive" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
        </>
      )}
      {!isLoading && filtered.length > 0 ? (
        <BarrePagination pagination={pagination} nom="mouvements" className="mt-0" />
      ) : null}
      {!isLoading ? (
        <p className="text-xs text-muted-foreground">
          {filtered.length} mouvement{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''}
          {' · '}
          Entrées : {formatVolume(sommeDes(filtered, ['IMPORT']))}
          {' · '}
          Sorties : {formatVolume(sommeDes(filtered, ['SALE', 'INTERNAL_CONSUMPTION', 'LOSS']))}
        </p>
      ) : null}
    </div>
  );
};

const sommeDes = (lignes, types) =>
  lignes
    .filter((m) => types.includes(m.movement_type))
    .reduce((somme, m) => somme + Number(m.quantity_liters || 0), 0);

const movementTypeLabel = (type, isControlFuel) => {
  const labels = isControlFuel
    ? {
        IMPORT: 'Entrée cuve',
        INTERNAL_CONSUMPTION: 'Sortie engin',
        INTERNAL_TRANSFER: 'Transfert interne',
        ADJUSTMENT: 'Ajustement',
        LOSS: 'Perte / écart',
        SALE: 'Sortie externe',
      }
    : {
        IMPORT: 'Entrée',
        SALE: 'Vente',
        INTERNAL_TRANSFER: 'Transfert interne',
        INTERNAL_CONSUMPTION: 'Consommation interne',
        ADJUSTMENT: 'Ajustement',
        LOSS: 'Perte',
      };
  return labels[type] || libelle('stockMovement', type);
};

const locationLabel = (location, isControlFuel, side = 'from', movement = null) => {
  // Un chargement part du dépôt pétrolier ou du port et arrive « en transit »:
  // afficher « En transit » des deux côtés ne disait pas d'où le carburant vient.
  if (
    !isControlFuel
    && movement?.movement_type === 'IN_TRANSIT'
    && movement?.from_location === 'IN_TRANSIT'
    && movement?.to_location === 'IN_TRANSIT'
  ) {
    return side === 'from' ? 'Dépôt pétrolier / port' : 'En transit';
  }
  const labels = isControlFuel
    ? {
        INTERNAL_TANK: 'Cuve interne',
        ASSET: 'Engin',
        PROJECT: 'Projet',
        DEPARTMENT: 'Département',
        DEPOT: 'Dépôt',
        STATION: 'Station',
        IN_TRANSIT: 'En transit',
      }
    : {
        DEPOT: 'Dépôt',
        IN_TRANSIT: 'En transit',
        STATION: 'Station',
        CUSTOMER: 'Client',
        CLIENT: 'Client',
        INTERNAL_TANK: 'Cuve interne',
        ASSET: 'Engin',
      };
  return labels[location] || libelle('stockLocation', location, '—');
};

export default StockMovementsTable;
